import { useState, useContext, useCallback, useRef } from "react";
import { getApiUrl } from "../utils/api";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { GoogleLogin } from "@react-oauth/google";
import { FaUser, FaLock, FaSpinner } from "react-icons/fa";
import Modal from "../components/Modal";
import { authSchemas } from "../validation/schemas";
import { validateForm } from "../validation/validate";
import useAuthFormKeyboardScroll from "../hooks/useAuthFormKeyboardScroll";
import "./Login.css";

function getSafeRedirectPath(from) {
  if (typeof from !== "string" || !from.startsWith("/") || from.startsWith("//")) {
    return "/";
  }
  return from;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const postLoginPath = getSafeRedirectPath(location.state?.from);
  const [form, setForm] = useState({ emailOrUsername: "", password: "" });
  const [formErrors, setFormErrors] = useState({});
  const { setUser } = useContext(UserContext);
  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetForm, setResetForm] = useState({
    resetToken: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [sending2FA, setSending2FA] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [showLogin, setShowLogin] = useState(false); // allow showing login while 2FA session exists
  const [twoFAUserId, setTwoFAUserId] = useState(null);
  const [twoFAOtp, setTwoFAOtp] = useState("");
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const authPanelRef = useAuthFormKeyboardScroll();

  // Refs for password inputs to prevent re-renders
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  const finishLogin = useCallback(
    (userData) => {
      if (!userData?.username) {
        navigate("/profile");
        return;
      }
      navigate(postLoginPath);
    },
    [navigate, postLoginPath]
  );

  const handleResetTokenChange = useCallback((e) => {
    setResetForm((prev) => ({ ...prev, resetToken: e.target.value }));
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    try {
      const res = await fetch(getApiUrl("/api/auth/google"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ token: credentialResponse.credential }),
      });

      const data = await res.json();

      if (res.ok) {
        // Store user for UI and token for Socket.IO auth
        localStorage.setItem("user", JSON.stringify(data.user));
        if (data.token) {
          localStorage.setItem("token", data.token);
        }
        setUser(data.user);

        let loggedInUser = data.user;

        // Fetch full profile
        try {
          const profileRes = await fetch(
            getApiUrl("/api/user/profile"),
            {
              credentials: "include",
            }
          );
          if (profileRes.ok) {
            const profileData = await profileRes.json();
            if (profileData.user) {
              localStorage.setItem("user", JSON.stringify(profileData.user));
              setUser(profileData.user);
              loggedInUser = profileData.user;
            }
          }
        } catch (profileError) {
          console.error(
            "Failed to fetch profile after Google login:",
            profileError
          );
        }

        finishLogin(loggedInUser);
      } else {
        setModal({
          open: true,
          title: "Error",
          message: data.error || data.msg || "Google login failed",
          type: "error",
        });
      }
    } catch (err) {
      setModal({
        open: true,
        title: "Error",
        message: "Network error. Please try again.",
        type: "error",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setModal({
      open: true,
      title: "Error",
      message: "Google login was cancelled or failed",
      type: "error",
    });
    setGoogleLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If there's already a pending 2FA session, switch back to 2FA form instead of re-logging
    if (twoFAUserId) {
      setRequires2FA(true);
      setShowLogin(false);
      return;
    }

    // Validate form data
    const validation = await validateForm(authSchemas.login, form);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    // Sanitize form data (emailOrUsername and password don't need HTML sanitization)
    // Just pass through the validated data
    const sanitizedForm = validation.data;

    setLoginLoading(true);
    try {
      const res = await fetch(getApiUrl("/api/auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // Include cookies in the request
        body: JSON.stringify(sanitizedForm),
      });
      const data = await res.json();

      if (res.ok) {
        // Check if 2FA is required
        if (data.requires2FA) {
          setSending2FA(true);
          setRequires2FA(true);
          setTwoFAUserId(data.userId);
          setLoginLoading(false); // Hide generic loader
          // Show 2FA-specific loader briefly, then show form
          setTimeout(() => {
            setSending2FA(false);
            setModal({
              open: true,
              title: "2FA Required",
              message: data.msg || "Please check your email for the 2FA code.",
              type: "info",
            });
          }, 500); // Brief delay to show loader
          return;
        }

        // Store user for UI and token for Socket.IO auth
        localStorage.setItem("user", JSON.stringify(data.user));
        if (data.token) {
          localStorage.setItem("token", data.token);
        }
        setUser(data.user);

        let loggedInUser = data.user;

        // Fetch full profile to ensure we have the latest data including avatar
        try {
          const profileRes = await fetch(
            getApiUrl("/api/user/profile"),
            {
              credentials: "include", // Include cookies for authentication
            }
          );
          if (profileRes.ok) {
            const profileData = await profileRes.json();
            if (profileData.user) {
              // Update with full profile data
              localStorage.setItem("user", JSON.stringify(profileData.user));
              setUser(profileData.user);
              loggedInUser = profileData.user;
            }
          }
        } catch (profileError) {
          // If profile fetch fails, continue with login data
          console.error("Failed to fetch profile after login:", profileError);
        }

        setLoginLoading(false);
        finishLogin(loggedInUser);
      } else {
        setLoginLoading(false);
        setModal({
          open: true,
          title: "Error",
          message: data.error || data.msg,
          type: "error",
        });
      }
    } catch (error) {
      setLoginLoading(false);
      setModal({
        open: true,
        title: "Error",
        message: "Network error. Please try again.",
        type: "error",
      });
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      setModal({
        open: true,
        title: "Error",
        message: "Please enter your email address",
        type: "error",
      });
      return;
    }

    setForgotLoading(true);
    try {
      const res = await fetch(
        getApiUrl("/api/auth/forgot-password"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: forgotEmail }),
        }
      );
      const data = await res.json();

      if (res.ok) {
        setModal({
          open: true,
          title: "Success",
          message: data.msg,
          type: "success",
        });
        setShowForgotPassword(false);
        setShowResetPassword(true);
      } else {
        setModal({
          open: true,
          title: "Error",
          message: data.msg || "Failed to send reset email",
          type: "error",
        });
      }
    } catch (error) {
      setModal({
        open: true,
        title: "Error",
        message: "Network error. Please try again.",
        type: "error",
      });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    // Read values from refs and state
    const newPassword = newPasswordRef.current?.value || "";
    const confirmPassword = confirmPasswordRef.current?.value || "";
    const resetToken = resetForm.resetToken;

    if (!forgotEmail || !resetToken || !newPassword || !confirmPassword) {
      setModal({
        open: true,
        title: "Error",
        message: "Please fill in all fields",
        type: "error",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setModal({
        open: true,
        title: "Error",
        message: "Passwords do not match",
        type: "error",
      });
      return;
    }

    if (newPassword.length < 6) {
      setModal({
        open: true,
        title: "Error",
        message: "Password must be at least 6 characters",
        type: "error",
      });
      return;
    }

    setResetLoading(true);
    try {
      const res = await fetch(getApiUrl("/api/auth/reset-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotEmail,
          resetToken: resetToken,
          newPassword: newPassword,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        setModal({
          open: true,
          title: "Success",
          message: data.msg,
          type: "success",
        });
        setShowResetPassword(false);
        setForgotEmail("");
        setResetForm({ resetToken: "", newPassword: "", confirmPassword: "" });
        // Clear password inputs
        if (newPasswordRef.current) newPasswordRef.current.value = "";
        if (confirmPasswordRef.current) confirmPasswordRef.current.value = "";
      } else {
        setModal({
          open: true,
          title: "Error",
          message: data.msg || "Failed to reset password",
          type: "error",
        });
      }
    } catch (error) {
      setModal({
        open: true,
        title: "Error",
        message: "Network error. Please try again.",
        type: "error",
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFAOtp || !twoFAUserId) {
      setModal({
        open: true,
        title: "Error",
        message: "Please enter the 2FA code",
        type: "error",
      });
      return;
    }

    setTwoFALoading(true);
    try {
      const res = await fetch(getApiUrl("/api/auth/verify-2fa"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userId: twoFAUserId, otp: twoFAOtp }),
      });
      const data = await res.json();

      if (res.ok) {
        // Store user for UI and token for Socket.IO auth
        localStorage.setItem("user", JSON.stringify(data.user));
        if (data.token) {
          localStorage.setItem("token", data.token);
        }
        setUser(data.user);

        let loggedInUser = data.user;

        // Fetch full profile
        try {
          const profileRes = await fetch(
            getApiUrl("/api/user/profile"),
            {
              credentials: "include",
            }
          );
          if (profileRes.ok) {
            const profileData = await profileRes.json();
            if (profileData.user) {
              localStorage.setItem("user", JSON.stringify(profileData.user));
              setUser(profileData.user);
              loggedInUser = profileData.user;
            }
          }
        } catch (profileError) {
          console.error("Failed to fetch profile after 2FA:", profileError);
        }

        setRequires2FA(false);
        setTwoFAOtp("");
        setTwoFAUserId(null);
        finishLogin(loggedInUser);
      } else {
        setModal({
          open: true,
          title: "Error",
          message: data.msg || data.error || "Invalid 2FA code",
          type: "error",
        });
      }
    } catch (error) {
      setModal({
        open: true,
        title: "Error",
        message: "Network error. Please try again.",
        type: "error",
      });
    } finally {
      setTwoFALoading(false);
    }
  };

  return (
    <div className="auth-page">
      {(loginLoading || sending2FA) && (
        <div className="auth-page__overlay">
          <div className="auth-page__overlay-card">
            <FaSpinner className="text-4xl text-[#f26522] animate-spin mx-auto mb-4" />
            <p className="font-medium text-[#333333] mb-1">
              {sending2FA
                ? "Sending 2FA code to your email..."
                : "Logging in..."}
            </p>
            <p className="text-sm text-[#777777]">Please wait</p>
          </div>
        </div>
      )}
      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onClose={() =>
          setModal({ open: false, title: "", message: "", type: "info" })
        }
      />

      <div className="auth-page__layout">
        <aside className="auth-page__brand">
          <Link to="/">
            <img
              src="/logo.png"
              srcSet="/logo.png 1x, /logo@2x.png 2x"
              alt=""
              className="auth-page__brand-logo"
            />
          </Link>
          <h2 className="auth-page__brand-title">Welcome back, poet.</h2>
          <p className="auth-page__brand-text">
            Sign in to share your work, join group discussions, and connect
            with writers who get it.
          </p>
          <blockquote className="auth-page__brand-quote">
            “Poetry is when an emotion has found its thought and the thought has
            found words.”
          </blockquote>
        </aside>

        <div className="auth-page__panel" ref={authPanelRef}>
          <div className="auth-page__card">
            <Link to="/">
              <img
                src="/logo.png"
                srcSet="/logo.png 1x, /logo@2x.png 2x"
                alt="PeerPoetry"
                className="auth-page__mobile-logo"
              />
            </Link>

            <h1 className="auth-page__title">
              {requires2FA && twoFAUserId && !showLogin
                ? "Verify your sign-in"
                : "Sign in"}
            </h1>
            <p className="auth-page__subtitle">
              {requires2FA && twoFAUserId && !showLogin
                ? "Enter the 6-digit code we sent to your email."
                : "Pick up where you left off."}
            </p>

            {requires2FA && twoFAUserId && !showLogin ? (
              <form onSubmit={handleVerify2FA} className="auth-page__form">
                <div className="auth-page__field">
                  <label htmlFor="twoFAOtp" className="auth-page__label">
                    Verification code
                  </label>
                  <input
                    id="twoFAOtp"
                    type="text"
                    name="twoFAOtp"
                    placeholder="000000"
                    value={twoFAOtp}
                    onChange={(e) => setTwoFAOtp(e.target.value)}
                    maxLength={6}
                    className="auth-page__input auth-page__input--center"
                    required
                    autoFocus
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                </div>
                <button
                  type="submit"
                  disabled={twoFALoading}
                  className="auth-page__submit flex items-center justify-center gap-2"
                >
                  {twoFALoading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Verifying…
                    </>
                  ) : (
                    "Verify & continue"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTwoFAUserId(null);
                    setRequires2FA(false);
                    setTwoFAOtp("");
                    setShowLogin(false);
                    setForm({ emailOrUsername: "", password: "" });
                    setFormErrors({});
                    setModal({ open: false, title: "", message: "", type: "info" });
                    navigate("/login", { replace: true });
                  }}
                  className="auth-page__link-btn w-full mt-1"
                >
                  Back to sign in
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="auth-page__form">
                <div className="auth-page__field">
                  <label htmlFor="emailOrUsername" className="auth-page__label">
                    Email or pen name
                  </label>
                  <div className="auth-page__input-wrap">
                    <FaUser className="auth-page__input-icon" aria-hidden="true" />
                    <input
                      id="emailOrUsername"
                      type="text"
                      name="emailOrUsername"
                      placeholder="you@email.com or pen name"
                      value={form.emailOrUsername}
                      onChange={handleChange}
                      className={`auth-page__input${
                        formErrors.emailOrUsername ? " auth-page__input--error" : ""
                      }`}
                      required
                      autoComplete="username"
                      aria-invalid={formErrors.emailOrUsername ? "true" : "false"}
                      aria-describedby={
                        formErrors.emailOrUsername
                          ? "emailOrUsername-error"
                          : undefined
                      }
                    />
                  </div>
                  {formErrors.emailOrUsername && (
                    <p
                      id="emailOrUsername-error"
                      className="auth-page__error"
                      role="alert"
                    >
                      {formErrors.emailOrUsername}
                    </p>
                  )}
                </div>

                <div className="auth-page__field">
                  <label htmlFor="password" className="auth-page__label">
                    Password
                  </label>
                  <div className="auth-page__input-wrap">
                    <FaLock className="auth-page__input-icon" aria-hidden="true" />
                    <input
                      id="password"
                      type="password"
                      name="password"
                      placeholder="Your password"
                      value={form.password}
                      onChange={handleChange}
                      className={`auth-page__input${
                        formErrors.password ? " auth-page__input--error" : ""
                      }`}
                      required
                      autoComplete="current-password"
                      aria-invalid={formErrors.password ? "true" : "false"}
                      aria-describedby={
                        formErrors.password ? "password-error" : undefined
                      }
                    />
                  </div>
                  {formErrors.password && (
                    <p id="password-error" className="auth-page__error" role="alert">
                      {formErrors.password}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    className="auth-page__link-btn"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="auth-page__submit flex items-center justify-center gap-2"
                >
                  {loginLoading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    "Sign in"
                  )}
                </button>
              </form>
            )}

            {!requires2FA && !twoFAUserId && (
              <>
                <div className="auth-page__divider">Or</div>
                <div className="auth-page__google">
                  {googleLoading ? (
                    <button
                      type="button"
                      disabled
                      className="auth-page__submit opacity-50 cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <FaSpinner className="animate-spin" />
                      Signing in with Google…
                    </button>
                  ) : (
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      useOneTap={false}
                      theme="outline"
                      size="large"
                      text="signin_with"
                      shape="rectangular"
                      logo_alignment="left"
                    />
                  )}
                </div>

                <div className="auth-page__footer">
                  <p className="auth-page__footer-text">New to PeerPoetry?</p>
                  <button
                    type="button"
                    className="auth-page__footer-link"
                    onClick={() => navigate("/register")}
                  >
                    Create an account
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showForgotPassword && (
        <div className="auth-page__modal">
          <div className="auth-page__modal-card">
            <h2>Forgot password</h2>
            <p>Enter your email and we&apos;ll send you a reset code.</p>
            <form onSubmit={handleForgotPassword} className="auth-page__form">
              <div className="auth-page__input-wrap">
                <FaUser className="auth-page__input-icon" aria-hidden="true" />
                <input
                  type="email"
                  placeholder="Email address"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="auth-page__input"
                  required
                  autoComplete="email"
                />
              </div>
              <div className="auth-page__modal-actions">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="auth-page__btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="auth-page__submit"
                  style={{ flex: 1, marginTop: 0 }}
                >
                  {forgotLoading ? "Sending…" : "Send code"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showResetPassword && (
        <div className="auth-page__modal">
          <div className="auth-page__modal-card">
            <h2>Reset password</h2>
            <p>Enter the code from your email and choose a new password.</p>
            <form onSubmit={handleResetPassword} noValidate className="auth-page__form">
              <input
                key="reset-token-input"
                type="text"
                placeholder="Reset code"
                value={resetForm.resetToken}
                onChange={handleResetTokenChange}
                className="auth-page__input"
                style={{ paddingLeft: "0.85rem" }}
                required
              />
              <div className="auth-page__input-wrap">
                <FaLock className="auth-page__input-icon" aria-hidden="true" />
                <input
                  key="new-password-input"
                  ref={newPasswordRef}
                  type="password"
                  placeholder="New password"
                  defaultValue=""
                  className="auth-page__input"
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="auth-page__input-wrap">
                <FaLock className="auth-page__input-icon" aria-hidden="true" />
                <input
                  key="confirm-password-input"
                  ref={confirmPasswordRef}
                  type="password"
                  placeholder="Confirm new password"
                  defaultValue=""
                  className="auth-page__input"
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="auth-page__modal-actions">
                <button
                  type="button"
                  onClick={() => setShowResetPassword(false)}
                  className="auth-page__btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="auth-page__submit"
                  style={{ flex: 1, marginTop: 0 }}
                >
                  {resetLoading ? "Resetting…" : "Reset password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
